import { useState, useEffect } from 'react';
import type { Platform } from '../types';

// BeforeInstallPromptEvent is not in standard TS lib, so we define it.
interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
  prompt(): Promise<void>;
}

const getPlatform = (): Platform => {
  const ua = navigator.userAgent;
  if (/android/i.test(ua)) return 'Android';
  // Check for iPhone, iPad, iPod, and ensure it's not a Windows Phone
  if (/iPad|iPhone|iPod/.test(ua) && !(window as any).MSStream) return 'iOS';
  if (/mac/i.test(ua)) return 'macOS';
  if (/win/i.test(ua)) return 'Windows';
  return 'Unknown';
};

export const usePWAInstall = () => {
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [platform, setPlatform] = useState<Platform>('Unknown');
  const [showIosInstallModal, setShowIosInstallModal] = useState(false);

  useEffect(() => {
    setPlatform(getPlatform());

    // Check if the app is already running in standalone mode.
    const checkInstalledStatus = () => {
        const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
        const isIosStandalone = (window.navigator as any).standalone === true;
        
        if (isStandalone || isIosStandalone) {
            setIsInstalled(true);
            setIsInstallable(false); // If installed, it's not installable again.
        }
    };

    checkInstalledStatus();

    // Listen for the browser's install prompt event.
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      // Only show the prompt if the app is not already installed.
      if (!isInstalled) {
        setInstallPrompt(e as BeforeInstallPromptEvent);
        setIsInstallable(true);
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Listen for changes in display mode (e.g., user installs the app).
    const mediaQueryList = window.matchMedia('(display-mode: standalone)');
    const handleDisplayChange = (e: MediaQueryListEvent) => {
        if (e.matches) {
            setIsInstalled(true);
            setIsInstallable(false);
            setInstallPrompt(null); // Clear the prompt event as it's no longer needed.
        }
    };
    mediaQueryList.addEventListener('change', handleDisplayChange);

    // Cleanup listeners on component unmount.
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      mediaQueryList.removeEventListener('change', handleDisplayChange);
    };
  }, [isInstalled]); // Re-run effect if `isInstalled` changes.

  const promptInstall = async () => {
    // 1. Do nothing if already installed.
    if (isInstalled) return;

    // 2. Handle iOS: Show our custom instructions modal.
    if (platform === 'iOS') {
        setShowIosInstallModal(true);
        return;
    }

    // 3. Handle standard PWA prompt.
    if (!installPrompt) {
        // Fallback for browsers that don't fire `beforeinstallprompt` but might have a manual option.
        alert('To install this app, look for an "Install" or "Add to Home Screen" option in your browser\'s menu.');
        return;
    }
    
    await installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;
    
    // 4. Update state based on user's choice and clear the prompt.
    if (outcome === 'accepted') {
      setIsInstalled(true);
      setIsInstallable(false);
    }
    setInstallPrompt(null);
  };
  
  return { isInstallable, isInstalled, promptInstall, platform, showIosInstallModal, setShowIosInstallModal };
};
